import { useEffect, useRef, useState } from 'react';
import { Button } from 'antd';
import {
  CameraFilled,
  CameraOutlined,
  ExclamationCircleFilled,
} from '@ant-design/icons';
import { useLanguage } from 'hooks/Language/useLanguage';

type CameraState =
  'idle' | 'starting' | 'live' | 'blocked' | 'unavailable';

/**
 * Live selfie capture. The selfie must be taken now with the front camera
 * (it is compared with the ID photo), so there is deliberately no file
 * upload here. The preview is mirrored like a mirror; the captured image is
 * the true, unmirrored frame.
 */
const SelfieCamera: React.FC<{
  onCapture: (file: File) => void;
  onCancel?: () => void;
  /** Open the camera straight away (retakes). */
  autoStart?: boolean;
}> = ({ onCapture, onCancel, autoStart }) => {
  const { translate } = useLanguage();
  const video = useRef<HTMLVideoElement>(null);
  const stream = useRef<MediaStream | null>(null);
  // Bumped by every start and stop, so a camera that opens after it is no
  // longer wanted (unmount, cancel, a newer start) is closed straight away.
  const session = useRef(0);
  const [state, setState] = useState<CameraState>('idle');

  const stop = () => {
    session.current += 1;
    stream.current?.getTracks().forEach((track) => track.stop());
    stream.current = null;
  };

  useEffect(() => {
    if (autoStart) start();
    return stop;
  }, []);

  const start = async () => {
    const current = ++session.current;
    if (!navigator.mediaDevices?.getUserMedia) {
      setState('unavailable');
      return;
    }
    setState('starting');
    try {
      const opened = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: { ideal: 1280 },
          height: { ideal: 960 },
        },
        audio: false,
      });
      if (current !== session.current) {
        opened.getTracks().forEach((track) => track.stop());
        return;
      }
      stream.current = opened;
      if (video.current) {
        video.current.srcObject = stream.current;
        await video.current.play();
      }
      setState('live');
    } catch (error) {
      if (current !== session.current) return;
      const name = (error as DOMException)?.name;
      setState(
        name === 'NotAllowedError' || name === 'SecurityError'
          ? 'blocked'
          : 'unavailable',
      );
    }
  };

  const capture = () => {
    const el = video.current;
    if (!el || !el.videoWidth) return;
    const canvas = document.createElement('canvas');
    canvas.width = el.videoWidth;
    canvas.height = el.videoHeight;
    canvas.getContext('2d')?.drawImage(el, 0, 0);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        stop();
        onCapture(
          new File([blob], `selfie-${Date.now()}.jpg`, {
            type: 'image/jpeg',
          }),
        );
      },
      'image/jpeg',
      0.92,
    );
  };

  const cancel = () => {
    stop();
    setState('idle');
    onCancel?.();
  };

  const problem = state === 'blocked' || state === 'unavailable';

  return (
    <div className={`kyc-selfie kyc-selfie--${state}`}>
      <div className="kyc-selfie__stage">
        <video ref={video} playsInline muted />
        {state === 'live' && <span className="kyc-selfie__oval" />}
        {!['live', 'starting'].includes(state) && (
          <div className="kyc-selfie__placeholder">
            <span
              className={`kyc-selfie__icon${problem ? ' is-error' : ''}`}
            >
              {problem ? (
                <ExclamationCircleFilled />
              ) : (
                <CameraFilled />
              )}
            </span>
            <strong>
              {state === 'blocked'
                ? translate('Camera access is blocked')
                : state === 'unavailable'
                  ? translate('No camera available')
                  : translate('Take a selfie')}
            </strong>
            <span>
              {state === 'blocked'
                ? translate(
                    'Allow camera access in your browser settings, then try again.',
                  )
                : state === 'unavailable'
                  ? translate(
                      'Use a device with a camera to take your selfie.',
                    )
                  : translate(
                      'We will compare it with the photo on your ID. It is taken live, not uploaded.',
                    )}
            </span>
          </div>
        )}
      </div>

      <div className="kyc-step__actions">
        {state === 'live' ? (
          <>
            <Button size="large" onClick={cancel}>
              {translate('Cancel')}
            </Button>
            <Button
              type="primary"
              size="large"
              className="kyc-btn-primary"
              icon={<CameraOutlined />}
              onClick={capture}
            >
              {translate('Capture')}
            </Button>
          </>
        ) : (
          <>
            {onCancel && (
              <Button size="large" onClick={cancel}>
                {translate('Cancel')}
              </Button>
            )}
            <Button
              type="primary"
              size="large"
              className="kyc-btn-primary"
              icon={<CameraOutlined />}
              loading={state === 'starting'}
              onClick={start}
            >
              {problem
                ? translate('Try again')
                : translate('Open camera')}
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default SelfieCamera;
